'use client';

import type { Member, Plan } from '@/app/(intakeFormV2)/intake-v2/types';
import { ActionButton } from '@/components/ui/ActionButton';
import { SalesforcePlanPricingResponse } from '@/types/salesforce';
import { isPediatricsEligible } from '@/utils/pediatrics';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { PLANS } from '../../common/PlanDetails';
import { MemberSummaryRow } from './MemberSummaryRow';
import { PlanRadioOption } from './PlanRadioOption';

export const QuoteEditMemberCard = ({
  member,
  memberIndex,
  pediatricsEnabled,
  pricing,
  onPlanChange,
  onEditingChange,
}: {
  member: Member;
  memberIndex: number;
  pediatricsEnabled: boolean;
  pricing?: SalesforcePlanPricingResponse;
  onPlanChange: (member: Member, plan: Plan) => void;
  onEditingChange: (editing: boolean) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(member.plan);
  // Shared across all `PlanRadioOption`s so the mobile "Learn more" accordion
  // stays open when the user switches between plans.
  const [planDetailsExpanded, setPlanDetailsExpanded] = useState(false);

  const pricingLookup: Record<Plan, number> = {
    Signature: pricing?.signaturePricing ?? 4000,
    Platinum: pricing?.platinumPricing ?? 12000,
    Pediatrics: pricing?.pediatricsPricing ?? 6000,
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedPlan(e.target.value as Plan);
  };

  const handleCancelPlanChange = useCallback(() => {
    setExpanded(false);
    onEditingChange(false);
    setSelectedPlan(member.plan);
  }, [member, onEditingChange]);

  const handleSavePlanChange = useCallback(() => {
    setExpanded(false);
    onEditingChange(false);
    onPlanChange(member, selectedPlan!);
  }, [member, selectedPlan, onPlanChange, onEditingChange]);

  return (
    <>
      <MemberSummaryRow
        member={member}
        expanded={expanded}
        onExpand={() => {
          setExpanded(true);
          onEditingChange(true);
        }}
      />
      {expanded && (
        <div className='border-t border-black'>
          <div className='my-8 lg:ml-[116px]'>
            {PLANS.filter(
              plan =>
                plan !== 'Pediatrics' ||
                (pediatricsEnabled &&
                  isPediatricsEligible(member.date_of_birth))
            ).map(plan => (
              <React.Fragment key={plan}>
                <PlanRadioOption
                  plan={plan}
                  memberIndex={memberIndex}
                  selectedPlan={selectedPlan}
                  currentPlan={member.plan}
                  price={pricingLookup[plan]}
                  onChange={handleChange}
                  detailsExpanded={planDetailsExpanded}
                  onToggleDetailsExpanded={setPlanDetailsExpanded}
                />
              </React.Fragment>
            ))}

            <div className='mt-2 flex w-full items-center gap-x-4'>
              <ActionButton
                label='Cancel'
                variant='secondary'
                type='button'
                onClick={handleCancelPlanChange}
                className='grow-1'
              />
              <ActionButton
                label='Save'
                variant='primary'
                type='button'
                onClick={handleSavePlanChange}
                className='grow-1'
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
